/**
 * UserPicker — enhanced tenant-user dropdown for the org chart.
 *
 * Displays the users list as a searchable combobox with two extra
 * signals:
 *   • **BLOCKED** (rose badge, disabled row) — user already sits on
 *     the ancestor-descendant lineage of the node we're editing. Same
 *     person can never be both above and below on the same path.
 *   • **KULLANIMDA** (zinc badge, still selectable) — user appears
 *     somewhere else in the chart. Informational so the operator can
 *     avoid duplicates but not forced.
 *
 * Falls back to the currently-selected value even if it's blocked so
 * the editor can render the existing choice without kicking the
 * operator out (allows Save to keep the same value untouched).
 */
import { useMemo, useRef, useState, useEffect } from "react";
import { Check, ChevronDown, Search, ShieldAlert, UserRound } from "lucide-react";

export function UserPicker({
  users,
  value,
  onChange,
  disabled,
  blockedIds, // Set of user_ids that can't be selected (lineage collision)
  usedIds, // Set of user_ids used elsewhere (badge only)
  testIdPrefix = "org-chart-user-picker",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selected = useMemo(
    () => (value ? users.find((u) => u.id === value) || null : null),
    [users, value],
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) => {
      const hay = `${u.name || ""} ${u.email || ""}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [users, q]);

  const pick = (u) => {
    if (u && blockedIds?.has(u?.id) && u?.id !== value) return; // blocked
    onChange(u ? u.id : "");
    setOpen(false);
    setQ("");
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testIdPrefix}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        data-testid={`${testIdPrefix}-trigger`}
        className={[
          "flex w-full items-center gap-2 rounded-md border border-zinc-300 bg-white px-2.5 py-2 text-left text-sm transition-colors",
          disabled
            ? "cursor-not-allowed bg-zinc-50 text-zinc-400"
            : "hover:border-zinc-400 focus:border-violet-500 focus:outline-none",
        ].join(" ")}
      >
        <UserRound className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
        <span
          className={[
            "min-w-0 flex-1 truncate",
            selected ? "text-zinc-900" : "text-zinc-400",
          ].join(" ")}
        >
          {selected
            ? `${selected.name}${selected.email ? ` · ${selected.email}` : ""}`
            : "— Serbest metin —"}
        </span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
      </button>

      {open && !disabled && (
        <div
          data-testid={`${testIdPrefix}-menu`}
          className="absolute z-50 mt-1 max-h-72 w-full overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-xl"
        >
          <div className="flex items-center gap-1.5 border-b border-zinc-100 px-2 py-1.5">
            <Search className="h-3.5 w-3.5 text-zinc-400" />
            <input
              type="text"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Kullanıcı ara…"
              data-testid={`${testIdPrefix}-search`}
              className="w-full bg-transparent text-sm outline-none placeholder:text-zinc-400"
            />
          </div>
          <ul className="max-h-56 overflow-y-auto py-1">
            <li>
              <button
                type="button"
                onClick={() => pick(null)}
                data-testid={`${testIdPrefix}-clear`}
                className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm text-zinc-500 hover:bg-zinc-50"
              >
                <span className="h-3.5 w-3.5 shrink-0" />
                <span className="italic">— Serbest metin —</span>
                {!value && <Check className="ml-auto h-3.5 w-3.5 text-violet-600" />}
              </button>
            </li>
            {filtered.length === 0 && (
              <li className="px-2.5 py-3 text-center text-xs text-zinc-400">
                Eşleşen kullanıcı yok
              </li>
            )}
            {filtered.map((u) => {
              const isBlocked = blockedIds?.has(u.id) && u.id !== value;
              const isUsed = !isBlocked && usedIds?.has(u.id) && u.id !== value;
              const isCurrent = u.id === value;
              return (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => pick(u)}
                    disabled={isBlocked}
                    data-testid={`${testIdPrefix}-option-${u.id}`}
                    className={[
                      "flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm transition-colors",
                      isBlocked
                        ? "cursor-not-allowed bg-rose-50/40 text-zinc-400"
                        : "text-zinc-700 hover:bg-zinc-50",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold uppercase text-white",
                        isBlocked ? "bg-rose-500" : "bg-zinc-800",
                      ].join(" ")}
                    >
                      {(u.name || u.email || "?").slice(0, 1)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate font-medium">{u.name}</span>
                        {isBlocked && (
                          <span
                            className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-rose-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-rose-700"
                            data-testid={`${testIdPrefix}-badge-blocked-${u.id}`}
                          >
                            <ShieldAlert className="h-2.5 w-2.5" strokeWidth={2.5} />
                            Üstte seçili
                          </span>
                        )}
                        {isUsed && (
                          <span
                            className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-800"
                            data-testid={`${testIdPrefix}-badge-used-${u.id}`}
                          >
                            Kullanımda
                          </span>
                        )}
                      </div>
                      <div className="truncate text-[10px] text-zinc-500">
                        {u.email}
                      </div>
                    </div>
                    {isCurrent && (
                      <Check className="h-3.5 w-3.5 shrink-0 text-violet-600" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
